/**
 * Card Resultado · Book Brasil (#251 / #253 / #255 rev2 — casar o alvo
 * `docs/resultado_card_alvo.html`) — bespoke do PnlAtacado (o `ResultadoTable`
 * genérico segue servindo o PnlVarejo mock #247, intocado).
 *
 * Header: título à esquerda + HERO "resultado no mês (VF)" à direita (fv_mensal,
 * R$ ABREVIADO, cor por sinal). Sparkline de tendência (variação acumulada real).
 * Linhas Δ (Diário/Semanal/Mensal/Anual) em 2 COLUNAS: label+descritor à esquerda;
 * valor COMPLETO sem centavos + "VP <valor> <chip>" à direita. Fontes UNIFORMES
 * (18px nas 4 linhas — sem hero-line). VF colorido por sinal (pos verde / neg
 * vermelho). Empty: Δ Anual = "aguardando baseline (31/12)". SEM coluna IPCA OFF.
 *
 * UI usa "VF" (Valor Futuro, PT) onde antes dizia "FV"; identificadores de código
 * (fv_diario, ResultadoFvResponse, …) seguem em inglês — só o texto exibido mudou.
 */
import { useState } from "react";
import { brDate } from "../../format";
import { fmtBRLcompactSigned, fmtBRLintSigned } from "../../utils/formatters";
import { useCurvaPnlSerie } from "../../hooks/useCurvaPnlSerie";
import { usePnlDeltaAnual } from "../../hooks/usePnlDeltaAnual";
import { ResultadoSparkline } from "./ResultadoSparkline";
import { NotaAnotacoesCorrecao } from "./NotaAnotacoesCorrecao";
import type { ResultadoFvResponse } from "../../api/types";

/** ISO "YYYY-MM-DD" → "DD/MM" (sem Date — imune a timezone). */
function ddmm(iso: string | null): string {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : "";
}

/** Cor por sinal do valor (casa --pos/--neg do alvo). */
function signCls(v: number | null): string {
  if (v === null || v === 0) return "text-slate-100";
  return v > 0 ? "text-emerald-400" : "text-red-400";
}

interface Linha {
  key: string;
  word: string;
  descr: string;
  fv: number | null;
  pv: number | null;
  anchor: string | null;
  baseline?: boolean;
  ipcaPendente?: boolean;  // indexado, mas falta o baseline da linha → mostra energia + marcador
}

/** Combina energia + IPCA de uma linha sob o modo. Nominal → energia pura.
 * Indexado: soma energia+IPCA quando há; pendente (sem baseline da linha) →
 * energia + flag (NÃO soma null/0). Exportada (#25a-fix): o MobilePnl espelha
 * o card com a MESMA lente — sem re-derivar. */
export function combinar(
  indexado: boolean,
  fvE: number | null, pvE: number | null,
  fvI: number | null | undefined, pvI: number | null | undefined,
  pendente: boolean,
): { fv: number | null; pv: number | null; ipcaPendente: boolean } {
  if (!indexado) return { fv: fvE, pv: pvE, ipcaPendente: false };
  if (pendente || fvI === null || fvI === undefined) {
    return { fv: fvE, pv: pvE, ipcaPendente: true };  // energia + marcador
  }
  return {
    fv: fvE === null ? null : fvE + fvI,
    pv: pvE === null || pvI === null || pvI === undefined ? pvE : pvE + pvI,
    ipcaPendente: false,
  };
}

/** Desconto a VP da linha = quanto o |VP| encolhe o |VF|. Math.round (sem
 * decimais na tela). null quando falta VF/VP ou VF=0. Exportada (#25a-fix). */
export function descontoChip(fv: number | null, pv: number | null): string | null {
  if (fv === null || pv === null || fv === 0) return null;
  const pct = Math.round((1 - Math.abs(pv) / Math.abs(fv)) * 100);
  return pct >= 0 ? `−${pct}%` : `+${Math.abs(pct)}%`;
}

export function ResultadoBookBrasil({ resultado }: { resultado: ResultadoFvResponse }) {
  const r = resultado;
  const serie = useCurvaPnlSerie(30);
  const da = usePnlDeltaAnual();
  const [showAnos, setShowAnos] = useState(false);
  // Toggle Nominal/Indexado (IPCA) — default Nominal (NÃO regride o card atual).
  // Espelha a Exposição (#30): perna fixa reajustada pela implícita #218; o
  // forward/marcação fica nominal.
  const [indexado, setIndexado] = useState(false);
  const dataRef = r.as_of ? brDate(r.as_of) : "—";

  // Δ Anual acende do endpoint /api/pnl/delta-anual (MtM vs foto 31/12) — não do
  // baseline 31/12 do snapshot (#67), que nunca entrou. tem_foto=false → mantém
  // "aguardando baseline (31/12)" (honest-empty, não mostra 0 como resultado).
  const dad = da.data;
  const temFoto = dad?.metadados.tem_foto ?? false;
  const curvaAsof = dad?.metadados.curva_asof ?? null;
  const pend = r.ipca_pendente;  // {diario,semanal,mensal} | null
  const anualIpcaPend = dad?.metadados.ipca_pendente ?? true;

  // Cada linha: energia (Nominal) ou energia+IPCA (Indexado), pendente honesto.
  const dWin = combinar(indexado, r.fv_diario, r.pv_diario, r.fv_diario_ipca, r.pv_diario_ipca, !!pend?.diario);
  const sWin = combinar(indexado, r.fv_semanal, r.pv_semanal, r.fv_semanal_ipca, r.pv_semanal_ipca, !!pend?.semanal);
  const mWin = combinar(indexado, r.fv_mensal, r.pv_mensal, r.fv_mensal_ipca, r.pv_mensal_ipca, !!pend?.mensal);
  const aWin = combinar(
    indexado,
    temFoto ? dad!.delta_anual_vf : null,
    temFoto ? dad!.delta_anual_vp : null,
    dad?.delta_anual_ipca_vf, dad?.delta_anual_ipca_vp, anualIpcaPend,
  );

  const linhas: Linha[] = [
    { key: "diario", word: "Diário", descr: "vs ontem", fv: dWin.fv, pv: dWin.pv, anchor: r.d1_date, ipcaPendente: dWin.ipcaPendente },
    { key: "semanal", word: "Semanal", descr: "vs fech. semana", fv: sWin.fv, pv: sWin.pv, anchor: r.dsem_date, ipcaPendente: sWin.ipcaPendente },
    { key: "mensal", word: "Mensal", descr: "vs fech. mês", fv: mWin.fv, pv: mWin.pv, anchor: r.dmes_date, ipcaPendente: mWin.ipcaPendente },
    {
      key: "anual", word: "Anual", descr: "vs marca 31/12",
      fv: aWin.fv, pv: aWin.pv,
      anchor: temFoto ? curvaAsof : null, baseline: true, ipcaPendente: aWin.ipcaPendente,
    },
  ];

  return (
    <div
      className="border border-white/10 rounded-2xl px-6 py-5 bg-indra-azul-surface"
      data-testid="resultado-book-brasil"
    >
      {/* HERO: título + toggle Nominal/Indexado à esquerda; resultado no mês à direita */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-white">Resultado</h3>
          <p className="text-xs text-slate-400 mt-0.5">Book Brasil · referência {dataRef}</p>
          {/* Toggle no TOPO do card (mesmo padrão da Exposição #30). Default Nominal. */}
          <div
            className="inline-flex rounded-md border border-white/10 bg-indra-azul-deep p-0.5 text-xs mt-2"
            role="group"
            aria-label="Modo de marcação do Resultado"
            data-testid="resultado-toggle-indexado"
          >
            <button
              type="button"
              onClick={() => setIndexado(false)}
              aria-pressed={!indexado}
              className={`px-2.5 py-1 rounded transition-colors ${!indexado ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
            >
              Nominal
            </button>
            <button
              type="button"
              onClick={() => setIndexado(true)}
              aria-pressed={indexado}
              className={`px-2.5 py-1 rounded transition-colors ${indexado ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
            >
              Indexado (IPCA)
            </button>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500">resultado no mês (VF)</div>
          <div
            className={`font-mono font-medium text-xl mt-0.5 ${signCls(mWin.fv)}`}
            data-testid="resultado-hero-mes"
          >
            {fmtBRLcompactSigned(mWin.fv)}
          </div>
        </div>
      </div>

      <div className="mt-4 mb-1">
        {/* #50 fail-closed: erro de API vira estado de ERRO no sparkline (a
            tela DIZ), nunca empty state mudo nem fallback. */}
        <ResultadoSparkline
          serie={serie.data.serie}
          ancoras={serie.data.ancoras}
          error={serie.error}
          anotacoes={serie.data.anotacoes ?? []}
        />
        {/* 28/09: a anotação sai onde o número sai — 1 linha por linha
            anotada DENTRO da janela da série (estado, nunca data). */}
        <NotaAnotacoesCorrecao anotacoes={serie.data.anotacoes ?? []} />
      </div>

      <div>
        {linhas.map((l) => {
          const chip = descontoChip(l.fv, l.pv);
          return (
            <div
              key={l.key}
              className="flex justify-between items-start py-3 border-t border-white/[0.07] first:border-t-0"
              data-testid={`resultado-linha-${l.key}`}
            >
              {/* ESQUERDA: Δ Período + descritor/data-âncora */}
              <div>
                <div className="text-[15px] font-medium text-slate-200">
                  <span className="text-slate-500">Δ</span> {l.word}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {l.descr}
                  {l.anchor ? ` · ${ddmm(l.anchor)}` : ""}
                </div>
              </div>

              {/* DIREITA: valor completo em cima + VP <valor> <chip> embaixo */}
              <div className="text-right">
                {l.fv === null ? (
                  l.baseline ? (
                    <div className="text-[13px] text-slate-500 italic" data-testid="resultado-anual-aguardando">
                      aguardando baseline (31/12)
                    </div>
                  ) : (
                    <div className="text-[13px] text-slate-500" data-testid={`resultado-vazio-${l.key}`}>
                      —
                    </div>
                  )
                ) : (
                  <>
                    <div
                      className={`font-mono tabular-nums font-extrabold text-lg ${signCls(l.fv)}`}
                      data-testid={`resultado-fv-${l.key}`}
                    >
                      {fmtBRLintSigned(l.fv)}
                    </div>
                    <div className="text-[13px] text-slate-400 font-mono mt-0.5" data-testid={`resultado-vp-${l.key}`}>
                      VP {l.pv === null ? "—" : fmtBRLintSigned(l.pv)}
                      {chip && (
                        <span
                          className="text-[10px] px-1.5 py-px rounded-[5px] bg-white/[0.07] text-slate-300 border border-white/10 ml-1.5"
                          title="desconto a valor presente vs VF"
                          data-testid={`resultado-chip-${l.key}`}
                        >
                          {chip}
                        </span>
                      )}
                    </div>
                    {/* Indexado mas falta o baseline da linha → energia + marcador honesto
                        (não somou null/0; mostra só a energia). */}
                    {l.ipcaPendente && (
                      <div
                        className="text-[10px] text-amber-300/70 mt-0.5"
                        title="implícita do início da janela ainda não disponível (baseline a backfillar)"
                        data-testid={`resultado-ipca-pendente-${l.key}`}
                      >
                        IPCA pendente
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Δ Anual por vértice (drill expansível) — 2027/2028 os maiores negativos */}
      {temFoto && dad && dad.por_ano.length > 0 && (
        <div className="mt-2 border-t border-white/[0.07] pt-2">
          <button
            type="button"
            onClick={() => setShowAnos((v) => !v)}
            className="text-[11px] text-slate-400 hover:text-slate-200"
            data-testid="resultado-anual-drill-toggle"
          >
            {showAnos ? "▾" : "▸"} Δ Anual por ano (VF · VP)
          </button>
          {showAnos && (
            <table className="w-full mt-2 text-[12px] font-mono" data-testid="resultado-anual-por-ano">
              <tbody>
                {dad.por_ano.map((a) => (
                  <tr key={a.ano} className="border-t border-white/[0.04]">
                    <td className="py-1 text-slate-400 font-sans">{a.ano}</td>
                    <td className={`py-1 text-right ${signCls(a.delta_vf)}`}>{fmtBRLintSigned(a.delta_vf)}</td>
                    <td className="py-1 text-right text-slate-400">
                      VP {a.delta_vp === null ? "—" : fmtBRLintSigned(a.delta_vp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <p className="text-[10px] text-slate-500 mt-3">
        VF/VP em R$ inteiros (sem centavos){r.curva_pre_asof ? ` · curva pré as-of ${brDate(r.curva_pre_asof)}` : ""}
      </p>

      {/* Rodapé do modo Indexado: explica a mecânica do IPCA (espelha a Exposição). */}
      {indexado && (
        <p className="text-[10px] text-amber-200/60 mt-2 leading-relaxed" data-testid="resultado-rodape-indexado">
          Indexado: perna fixa reajustada pela inflação implícita (#218); marcação/forward
          fica nominal.
          {r.curva_pre_asof ? ` Curva pré as-of ${brDate(r.curva_pre_asof)}.` : ""}
          {dad?.metadados.implicita_31_asof ? ` Implícita 31/12 as-of ${brDate(dad.metadados.implicita_31_asof)}.` : ""}
        </p>
      )}

      {/* Rodapé Δ Anual: fonte BBCE + caveat card-level CONTEXTUAL ao modo. */}
      {temFoto && (
        <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1.5" data-testid="resultado-anual-rodape">
          Δ Anual: vs marcação BBCE de 31/12
          <span className="px-2 py-0.5 rounded text-[10px] bg-white/5 text-white/40 border border-white/10">
            {indexado
              ? anualIpcaPend
                ? "energia · IPCA pendente (baseline 31/12)"
                : "energia + IPCA"
              : "componente energia · Indexado mostra IPCA"}
          </span>
        </p>
      )}

      {/* Nota-conceito (texto aprovado) — embaixo da tabela */}
      {temFoto && (
        <div
          className="mt-3 pt-3 border-t border-white/[0.07] text-[11px] leading-relaxed text-slate-400"
          data-testid="resultado-anual-nota-conceito"
        >
          <span className="text-slate-300 font-medium">Como ler o Δ Anual.</span>{" "}
          Variação de marcação do book desde a foto de 31/12/2025 (marcação BBCE de fim de
          ano), em VF (nominal) e VP (descontado pela curva pré, como as demais linhas). Num
          número só, junta o realizado dos meses já entregues no ano (liquidados a PLD, medidos
          contra a marca de 31/12) + a remarcação do book ainda aberto (forward de hoje vs
          31/12). O realizado do ano corrente já está embutido no Δ MtM — não é linha à parte.
          Marcação a mercado · componente energia (IPCA a seguir) · curva de hoje:{" "}
          {curvaAsof ? brDate(curvaAsof) : "—"}.
        </div>
      )}
    </div>
  );
}
